/**
 * Phone number formatting utilities
 * Supports international and German phone number formats
 */

export const formatPhoneNumber = (value) => {
  if (!value) return '';

  // Remove all non-digit characters except + for international
  const cleaned = value.replace(/[^\d+]/g, '');

  // Handle international numbers (starting with +)
  if (cleaned.startsWith('+')) {
    return formatInternational(cleaned);
  }

  // Handle German numbers (starting with 0 or no prefix)
  return formatGerman(cleaned);
};

const formatInternational = (phone) => {
  // Special handling for German international numbers (+49)
  if (phone.startsWith('+49')) {
    const nationalNumber = phone.slice(3); // Remove +49
    // Format the national part as a German number
    const formattedNational = formatGermanWithLeadingZero(nationalNumber);
    return '+49 ' + formattedNational;
  }

  // General international format for other countries
  const match = phone.match(/^(\+\d{1,3})(\d{0,3})(\d{0,4})(\d{0,4})$/);

  if (!match) return phone;

  const [, country, area, local1, local2] = match;

  let formatted = country;
  if (area) formatted += ' ' + area;
  if (local1) formatted += ' ' + local1;
  if (local2) formatted += ' ' + local2;

  return formatted;
};

const formatGerman = (phone) => {
  // German phone number formats
  // Remove leading +49 if present
  const cleaned = phone.replace(/^\+49/, '');

  // Handle numbers starting with 0 (already formatted)
  if (cleaned.startsWith('0')) {
    return formatGermanWithLeadingZero(cleaned);
  }

  // Handle numbers without leading zero (add +49)
  return formatGermanWithoutLeadingZero(cleaned);
};

const formatGermanWithLeadingZero = (phone) => {
  // Handle numbers that start with 0 (traditional German format)
  if (phone.startsWith('0')) {
    const patterns = [
      // Mobile: 015X XXX XXXX (11 digits total)
      /^(\d{4})(\d{3})(\d{4})$/,
      // Landline: 0XXX XX XX (9-10 digits total)
      /^(\d{4})(\d{2})(\d{2})$/,
      // Various other formats
      /^(\d{4})(\d{3})(\d{3})$/,
      /^(\d{3})(\d{3})(\d{4})$/,
      /^(\d{3})(\d{4})(\d{3})$/
    ];

    for (const pattern of patterns) {
      const match = phone.match(pattern);
      if (match) {
        return match.slice(1).join(' ');
      }
    }
  }

  // Handle numbers without leading 0 (like 1512345678 -> 151 234 5678)
  // This is for international format national numbers
  const patterns = [
    // Mobile format: XXX XXX XXXX (10 digits: 151 234 5678)
    /^(\d{3})(\d{3})(\d{4})$/,
    // Alternative mobile: XXXX XXX XXX (10 digits: 1512 345 678)
    /^(\d{4})(\d{3})(\d{3})$/,
    // Landline format: XX XXX XXX (9 digits: 30 123 4567)
    /^(\d{2})(\d{3})(\d{4})$/,
    // General fallback: groups of 3 with remainder
    /^(\d{3})(\d{3})?(\d{1,4})?$/
  ];

  for (const pattern of patterns) {
    const match = phone.match(pattern);
    if (match) {
      // Filter out undefined groups and join
      const groups = match.slice(1).filter(group => group !== undefined && group !== '');
      if (groups.length > 1) {
        return groups.join(' ');
      }
    }
  }

  // Default: add space every 3 digits for longer numbers
  return phone.replace(/(\d{3})(?=\d)/g, '$1 ');
};

const formatGermanWithoutLeadingZero = (phone) => {
  // For numbers without leading zero, assume they are German and add +49
  // Format as: +49 XXX XXX XXX or +49 XXXX XXX XXX

  // Use specific German formatting for +49 numbers
  if (phone.length >= 10) {
    // Long number: +49 XXX XXX XXXX
    return `+49 ${phone.slice(0, 3)} ${phone.slice(3, 6)} ${phone.slice(6)}`;
  } else if (phone.length >= 9) {
    // Medium number: +49 XXX XXX XXX
    return `+49 ${phone.slice(0, 3)} ${phone.slice(3, 6)} ${phone.slice(6)}`;
  } else {
    // Short number: just add +49 and space every 3 digits
    return `+49 ${phone.replace(/(\d{3})(?=\d)/g, '$1 ')}`;
  }
};

// Validation function
export const validatePhoneNumber = (phone) => {
  if (!phone || phone.trim() === '') {
    return { valid: true, formatted: phone, errorKey: null };
  }

  // Allow formats:
  // - International: +4915123456789 (+ at start, then digits)
  // - National: 01512345678 or 030123456 (digits only, no +)
  if (!/^(\+\d+|\d+)$/.test(phone)) {
    return { valid: false, formatted: phone, errorKey: 'phoneNumberInvalidChars' };
  }

  // Basic validation rules: 7-15 digits (excluding + if present)
  const digitsOnly = phone.replace(/^\+/, '');
  const isValidLength = digitsOnly.length >= 7 && digitsOnly.length <= 15;

  const valid = isValidLength;

  return {
    valid,
    formatted: phone,
    errorKey: valid ? null : 'phoneNumberInvalidLength'
  };
};

// Get raw digits for storage/API calls
export const getRawPhoneNumber = (formatted) => {
  return formatted.replace(/[^\d+]/g, '');
};

// Format for display
export const formatForDisplay = (phone) => {
  if (!phone) return '';
  return formatPhoneNumber(phone);
};
